/**
 * Utility functions for week calculations using ISO 8601 standard
 * Week 1 is the first week that contains at least 4 days of the new year
 */

export interface WeekInfo {
  weekNumber: number;
  startDate: Date;
  endDate: Date;
  weekKey: string; // Format: YYYYWW (e.g., "202531")
  year: number;
}

/**
 * Get the current week number for the current year
 */
export function getCurrentWeek(): number {
  const now = new Date();
  return getWeekNumber(now);
}

/**
 * Check if a week is in the past (compared to current week/year)
 */
export function isWeekInPast(week: WeekInfo): boolean {
  const currentWeekNum = getCurrentWeek();
  const currentYear = new Date().getFullYear();
  const weekYear = new Date(week.startDate).getFullYear();

  if (weekYear < currentYear) {
    return true;
  } else if (weekYear > currentYear) {
    return false;
  } else {
    // Same year, compare week numbers
    
    return week.weekNumber < currentWeekNum && week.startDate < new Date();
  }
}

/**
 * Get the week number for a given date using ISO 8601 standard
 */
export function getWeekNumber(date: Date): number {
  const tempDate = new Date(date.getTime());
  
  // Set to Thursday of the same week (ISO week always contains Thursday)
  tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7));
  
  // Get the year of this Thursday
  const year = tempDate.getFullYear();
  
  // Get January 1st of that year
  const yearStart = new Date(year, 0, 1);
  
  // Calculate week number
  const weekNumber = Math.ceil((((tempDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  
  return weekNumber;
}

/**
 * Get the year that contains the ISO week for a given date
 */
export function getWeekYear(date: Date): number {
  const tempDate = new Date(date.getTime());
  
  // Set to Thursday of the same week (ISO week always contains Thursday)
  tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7));
  
  return tempDate.getFullYear();
}

/**
 * Convert week number and year to the Monday (start date) of that week
 */
export function weekToDate(year: number, weekNumber: number): Date {
  // Find January 4th of the given year (always in week 1)
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay();
  const daysFromMonday = jan4Day === 0 ? 6 : jan4Day - 1; // Sunday = 0, so 6 days from Monday
  
  // Find the Monday of week 1
  const firstMondayOfWeek1 = new Date(jan4);
  firstMondayOfWeek1.setDate(jan4.getDate() - daysFromMonday);

  // Calculate the Monday of the requested week
  const weekStart = new Date(firstMondayOfWeek1);
  weekStart.setDate(firstMondayOfWeek1.getDate() + (weekNumber - 1) * 7);

  return weekStart;
}

/**
 * Convert a date to week number and year
 */
export function dateToWeek(date: Date): { weekNumber: number; year: number } {
  return {
    weekNumber: getWeekNumber(date),
    year: getWeekYear(date)
  };
}

/**
 * Get complete week information (start date, end date, week key) for a given week
 */
export function getWeekInfo(year: number, weekNumber: number): WeekInfo {
  const startDate = weekToDate(year, weekNumber);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 4); // Friday

  return {
    weekNumber,
    startDate,
    endDate,
    weekKey: formatWeek(year, weekNumber).toString(),
    year
  };
}

/**
 * Format week as YYYYWW number
 */
export function formatWeek(year: number, weekNumber: number): number {
  return year * 100 + weekNumber;
}

/**
 * Parse week key (YYYYWW) to year and week number
 */
export function parseWeekKey(weekKey: string): { year: number; weekNumber: number } {
  const year = parseInt(weekKey.substring(0, 4));
  const weekNumber = parseInt(weekKey.substring(4, 6));
  return { year, weekNumber };
}

/**
 * Generate an array of week information for a range of weeks
 */
export function generateWeekRange(
  startYear: number, 
  startWeek: number, 
  numberOfWeeks: number
): WeekInfo[] {
  const weeks: WeekInfo[] = [];
  let currentWeekStart = weekToDate(startYear, startWeek);
  
  for (let i = 0; i < numberOfWeeks; i++) {
    // Get week info for the current date
    const year = getWeekYear(currentWeekStart);
    const weekNum = getWeekNumber(currentWeekStart);
    
    weeks.push(getWeekInfo(year, weekNum));
    
    // Move to next week (add 7 days)
    // Create new date object to avoid reference issues
    const nextWeek = new Date(currentWeekStart);
    nextWeek.setDate(nextWeek.getDate() + 7);
    currentWeekStart = nextWeek;
  }
  
  return weeks;
}

/**
 * Convert week key (YYYYWW) to week number (1-52)
 */
export function weekKeyToWeekNumber(weekKey: string): number {
  return parseInt(weekKey.substring(4, 6));
}

/**
 * Validate if a date range spans across weeks correctly
 */
export function validateWeekRange(
  startYear: number, 
  startWeek: number, 
  endYear: number, 
  endWeek: number
): boolean {
  const startDate = weekToDate(startYear, startWeek);
  const endDate = weekToDate(endYear, endWeek);
  
  return endDate > startDate;
}

/**
 * Format a week info object as a readable string for tooltips
 */
export function formatWeekTooltip(weekInfo: WeekInfo): string {
  return `Week ${weekInfo.weekNumber}: ${weekInfo.startDate.toLocaleDateString()} - ${weekInfo.endDate.toLocaleDateString()}`;
}
